import React, { createContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { User as DbUser, Tenant } from '@shared/types';

type UserRole = 'personal' | 'student' | 'influencer' | 'admin';

export interface UserProfile extends DbUser {
    role: UserRole;
}

export interface AuthContextType {
    user: User | null;
    session: Session | null;
    profile: UserProfile | null;
    profiles: UserProfile[]; // All profiles for this user
    tenant: Tenant | null;
    loading: boolean;
    activeRole: UserRole | null;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, data: SignUpData) => Promise<void>;
    signOut: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    refreshProfile: () => Promise<void>;
    switchRole: (role: UserRole) => void;
    hasMultipleProfiles: boolean;
}

interface SignUpData {
    full_name: string;
    role: UserRole;
    phone?: string;
    affiliate_code?: string;
    invite_code?: string;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [profiles, setProfiles] = useState<UserProfile[]>([]);
    const [activeRole, setActiveRole] = useState<UserRole | null>(null);
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [loading, setLoading] = useState(true);

    // Get active profile based on current role
    const profile = profiles.find(p => p.role === activeRole) || profiles[0] || null;
    const hasMultipleProfiles = profiles.length > 1;

    const fetchProfiles = useCallback(async (userId: string) => {
        try {
            // Fetch all profiles for this user (they may have multiple roles)
            const { data: profileData, error: profileError } = await supabase
                .from('users')
                .select('*')
                .eq('id', userId);

            if (profileError) {
                console.error('Error fetching profiles:', profileError);
                return;
            }

            const userProfiles = (profileData || []) as UserProfile[];
            setProfiles(userProfiles);

            // Restore active role from localStorage or use first profile's role
            const storedRole = localStorage.getItem('gofit-active-role');
            const validStoredRole = userProfiles.find(p => p.role === storedRole);

            if (validStoredRole) {
                setActiveRole(storedRole as UserRole);
            } else if (userProfiles.length > 0) {
                setActiveRole(userProfiles[0].role);
                localStorage.setItem('gofit-active-role', userProfiles[0].role);
            }

            // If personal, fetch tenant
            const personalProfile = userProfiles.find(p => p.role === 'personal');
            if (personalProfile?.tenant_id) {
                const { data: tenantData, error: tenantError } = await supabase
                    .from('tenants')
                    .select('*')
                    .eq('id', personalProfile.tenant_id)
                    .single();

                if (!tenantError && tenantData) {
                    setTenant(tenantData);
                }
            }
        } catch (error) {
            console.error('Error in fetchProfiles:', error);
        }
    }, []);

    useEffect(() => {
        // Get initial session
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) {
                fetchProfiles(session.user.id);
            }
            setLoading(false);
        });

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (_event, session) => {
                setSession(session);
                setUser(session?.user ?? null);

                if (session?.user) {
                    await fetchProfiles(session.user.id);
                } else {
                    setProfiles([]);
                    setActiveRole(null);
                    setTenant(null);
                }

                setLoading(false);
            }
        );

        return () => {
            subscription.unsubscribe();
        };
    }, [fetchProfiles]);

    const switchRole = (role: UserRole) => {
        const targetProfile = profiles.find(p => p.role === role);
        if (targetProfile) {
            setActiveRole(role);
            localStorage.setItem('gofit-active-role', role);

            // Update tenant if switching to personal
            if (role === 'personal' && targetProfile.tenant_id) {
                supabase
                    .from('tenants')
                    .select('*')
                    .eq('id', targetProfile.tenant_id)
                    .single()
                    .then(({ data }) => {
                        if (data) setTenant(data);
                    });
            } else {
                setTenant(null);
            }
        }
    };

    const signIn = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) throw error;
    };

    const signUp = async (email: string, password: string, data: SignUpData) => {
        // Check if user already exists with this email
        const { data: existingAuth } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        let userId: string;

        if (existingAuth?.user) {
            // User already exists, just add new profile
            userId = existingAuth.user.id;

            // Check if profile with this role already exists
            const { data: existingProfile } = await supabase
                .from('users')
                .select('id')
                .eq('id', userId)
                .eq('role', data.role)
                .single();

            if (existingProfile) {
                throw new Error('Você já possui um perfil com este tipo de conta.');
            }
        } else {
            // Create new auth user
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        full_name: data.full_name,
                        role: data.role,
                    },
                },
            });

            if (authError) throw authError;
            if (!authData.user) throw new Error('Erro ao criar conta');

            userId = authData.user.id;
        }

        // Create user profile for this role
        const { error: profileError } = await supabase.from('users').insert({
            id: userId,
            email,
            full_name: data.full_name,
            phone: data.phone,
            role: data.role,
        });

        if (profileError) {
            // If duplicate key error (profile already exists for this user+role), give friendly message
            if (profileError.code === '23505') {
                throw new Error('Este perfil já existe. Tente fazer login.');
            }
            console.error('Error creating profile:', profileError);
            throw profileError;
        }

        // If personal, create tenant
        if (data.role === 'personal') {
            const slug = data.full_name
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '')
                .slice(0, 50);

            const { data: tenantData, error: tenantError } = await supabase
                .from('tenants')
                .insert({
                    name: `${data.full_name}'s Gym`,
                    slug: `${slug}-${Date.now().toString(36)}`,
                    owner_id: userId,
                })
                .select()
                .single();

            if (tenantError) {
                console.error('Error creating tenant:', tenantError);
            } else {
                // Update user with tenant_id
                await supabase
                    .from('users')
                    .update({ tenant_id: tenantData.id })
                    .eq('id', userId)
                    .eq('role', 'personal');
            }
        }

        // Apply affiliate code if provided
        if (data.affiliate_code) {
            try {
                await supabase.rpc('apply_affiliate_code', {
                    p_user_id: userId,
                    p_code: data.affiliate_code,
                    p_source: 'code',
                });
            } catch (error) {
                console.error('Error applying affiliate code:', error);
            }
        }

        // Process invite code if provided
        if (data.invite_code && data.role === 'student') {
            try {
                const { error: inviteError } = await supabase
                    .from('student_pairings')
                    .update({
                        student_id: userId,
                        status: 'active',
                        paired_at: new Date().toISOString(),
                    })
                    .eq('invite_code', data.invite_code);

                if (inviteError) console.error('Error applying invite code:', inviteError);
            } catch (error) {
                console.error('Error in invite processing:', error);
            }
        }
    };

    const signOut = async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        setProfiles([]);
        setActiveRole(null);
        setTenant(null);
        localStorage.removeItem('gofit-active-role');
    };

    const resetPassword = async (email: string) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
    };

    const refreshProfile = async () => {
        if (user) {
            await fetchProfiles(user.id);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                session,
                profile,
                profiles,
                tenant,
                loading,
                activeRole,
                signIn,
                signUp,
                signOut,
                resetPassword,
                refreshProfile,
                switchRole,
                hasMultipleProfiles,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
